import { FC, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { BRAND, CHART_INK } from './agencyChartTheme';
import { ChevronDownIcon, ChevronUpIcon } from '../../theme/Icons';
import { DEFINITION_PARTS, IOutcomeDefinition } from './agencyOutcomeDefinitions';

const useStyles = createUseStyles({
    section: {
        marginBottom: 14,
        borderRadius: 10,
        border: `1px solid ${BRAND.tealEdge}`
    },

    toggle: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: '9px 12px',
        border: 'none',
        background: 'none',
        font: 'inherit',
        fontSize: 12.5,
        fontWeight: 600,
        color: BRAND.deepTeal,
        textAlign: 'left' as const,
        cursor: 'pointer'
    },

    toggleLabel: {
        flex: 1
    },

    parts: {
        margin: 0,
        padding: '2px 12px 12px'
    },

    heading: {
        marginTop: 10,
        fontSize: 11.5,
        fontWeight: 600,
        letterSpacing: '0.03em',
        textTransform: 'uppercase' as const,
        color: CHART_INK.label
    },

    body: {
        margin: '3px 0 0',
        fontSize: 12.5,
        lineHeight: 1.55,
        color: CHART_INK.strongLabel
    }
});

/**
 * How a figure is counted, inside the list it opens into.
 *
 * Here rather than in a tip on the card because the card is already the button that opens this list,
 * and a reader putting a figure in front of somebody else needs the whole definition, not a sentence.
 * Closed by default: the names are what most readers open a figure for.
 */
export const OutcomeDefinition: FC<{ definition: IOutcomeDefinition }> = ({ definition }) => {
    const classes = useStyles();
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className={classes.section} data-id="outcome-definition">
            <button
                type="button"
                className={classes.toggle}
                data-id="outcome-definition-toggle"
                aria-expanded={isOpen}
                onClick={() => setIsOpen(!isOpen)}
            >
                <span className={classes.toggleLabel}>How this is counted</span>
                {isOpen ? <ChevronUpIcon size="small" /> : <ChevronDownIcon size="small" />}
            </button>

            {isOpen && (
                <dl className={classes.parts}>
                    {DEFINITION_PARTS.map(part => (
                        <div key={part.key} data-id="outcome-definition-part" data-part={part.key}>
                            <dt className={classes.heading}>{part.heading}</dt>
                            <dd className={classes.body}>{definition[part.key]}</dd>
                        </div>
                    ))}
                </dl>
            )}
        </div>
    );
};
